import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Sparkles, BookOpen, Brain, Zap, Crown } from 'lucide-react';
export function PSIProDifferenceSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-2696" data-mp="12ixm33iv5f4n">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-2697" data-mp="tk3pve9g2qrm" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-2698" data-mp="5tzq5hzdnb5w" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-2699" data-mp="9x5tagr70m85" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-2700" data-mp="2wqcmuou9lojm">
        {/* Decorative header */}
        <div className="flex items-center justify-center gap-4 mb-12" data-id="element-2701" data-mp="2izze4k9ju5t8">
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-amber-500/50" data-id="element-2702" data-mp="2j097mhtsyv3o" />
          <Sparkles className="w-8 h-8 text-amber-500" data-id="element-2703" data-mp="2dys6qmbbj8n3" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-500/50 to-amber-500/50" data-id="element-2704" data-mp="2squbajoc2ptg" />
        </div>

        {/* Main headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-10 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
      }} data-id="element-2705" data-mp="2nx7viimvi5tf">
          But Here's What Makes{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-2706" data-mp="2ky0zs24ax0t7">
            PSI PRO
          </span>{' '}
          Different From Just "Getting A Course":
        </h2>

        {/* Body content */}
        <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-2707" data-mp="2yrs3gtvcpqh0">
          <p className="text-2xl font-bold text-stone-100" data-id="element-2708" data-mp="221rhf2md9o3e">Look.</p>

          <p data-id="element-2709" data-mp="2621ko7iry0ek">
            You could buy{' '}
            <span className="font-bold text-amber-400" data-id="element-2710" data-mp="2b0ctcexc24v7">
              Peptide Mastery 2.0
            </span>{' '}
            as a standalone product.
          </p>

          <p className="text-stone-400 italic pl-6 border-l-2 border-stone-700" data-id="element-2711" data-mp="2g10rrlnw5nzg">
            (Included in every PSI PRO membership — no separate purchase
            needed.)
          </p>

          <p className="font-semibold text-stone-100" data-id="element-2712" data-mp="3rd1njkj6acop">
            And it would be worth every penny.
          </p>

          {/* Feature list */}
          <div className="bg-gradient-to-br from-amber-950/30 to-black border-l-4 border-amber-500 p-5 sm:p-8 my-8 space-y-3" data-id="element-2713" data-mp="1as6hns10gjz1">
            <p className="flex items-start gap-3" data-id="element-2714" data-mp="160ofx1r5yfd0">
              <span className="text-amber-400 flex-shrink-0" data-id="element-2715" data-mp="15z3i939jygax">✓</span>
              <span className="text-stone-100" data-id="element-2716" data-mp="111twpypaaosd">130+ peptides decoded.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-2717" data-mp="x1jg7s3v8fz9">
              <span className="text-amber-400 flex-shrink-0" data-id="element-2718" data-mp="s1ooxng6hodo">✓</span>
              <span className="text-stone-100" data-id="element-2719" data-mp="s0dljoqnain3">54 proven protocols.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-2720" data-mp="n33wwbb2yb0b">
              <span className="text-amber-400 flex-shrink-0" data-id="element-2721" data-mp="i3r3t8tm87pa">✓</span>
              <span className="text-stone-100" data-id="element-2722" data-mp="i5lkuc1rnfo3">
                1,193 scientific citations.
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-2723" data-mp="2chs1vrhccqob">
              <span className="text-amber-400 flex-shrink-0" data-id="element-2724" data-mp="2cibhh1jir2z6">✓</span>
              <span className="text-stone-100" data-id="element-2725" data-mp="2kj9ej2zyn069">Mass spec verification.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-2726" data-mp="2phkpjh1zrgq2">
              <span className="text-amber-400 flex-shrink-0" data-id="element-2727" data-mp="2pf860zgl4yaw">✓</span>
              <span className="text-stone-100" data-id="element-2728" data-mp="2aj8rb5yyzzws">Lifetime updates.</span>
            </p>
          </div>

          <p className="text-xl font-bold text-center text-amber-500" style={{
          textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
        }} data-id="element-2729" data-mp="2fhk0rflcs8eh">
            It's the most comprehensive peptide education on the planet.
          </p>

          {/* Transition */}
          <div className="py-8" data-id="element-2730" data-mp="1ru2fm2wlg8kh">
            <p className="text-2xl font-bold text-stone-100" data-id="element-2731" data-mp="1rv3smcx6754u">
              But here's the thing...
            </p>
          </div>

          <p className="text-xl font-bold text-stone-100 border-l-4 border-purple-500 pl-6 py-2 bg-gradient-to-r from-purple-950/30 to-transparent" data-id="element-2732" data-mp="1wqaq32zujdlb">
            Having the information is only HALF the battle.
          </p>

          <p className="text-xl font-bold text-stone-100" data-id="element-2733" data-mp="145vww9tn1dl6">The other half?</p>

          {/* The paradigm shift section */}
          <div className="space-y-4 pl-6 border-l-2 border-cyan-500/30" data-id="element-2734" data-mp="z843tch9blfe">
            <p className="text-cyan-400 font-bold" data-id="element-2735" data-mp="1d6lp73jowlu5">
              Making the paradigm shift.
            </p>
            <p data-id="element-2736" data-mp="18790ed99d4ox">Understanding the PRINCIPLES behind the protocols.</p>
            <p data-id="element-2737" data-mp="18a3k22kg6lha">
              Knowing <span className="font-bold text-stone-100" data-id="element-2738" data-mp="1n4huhepy4mkc">HOW</span> to
              think about biology at the level Brenden and Ali do.
            </p>
            <p data-id="element-2739" data-mp="1i5oixru7z5mt">Learning to see what mainstream practitioners miss.</p>
            <p data-id="element-2740" data-mp="1vwa27hnl5hl8">
              Developing the ability to design your{' '}
              <span className="font-bold text-stone-100 uppercase" data-id="element-2741" data-mp="1rv810va3yvgm">OWN</span>{' '}
              protocols for{' '}
              <span className="font-bold text-stone-100 uppercase" data-id="element-2742" data-mp="25upqzdz54erk">YOUR</span>{' '}
              specific situation.
            </p>
            <p data-id="element-2743" data-mp="20y96ctr5gij8">
              Operating with the kind of certainty that comes from{' '}
              <span className="font-bold text-purple-400" data-id="element-2744" data-mp="ykl2ygqby101">MASTERY</span> instead
              of just following templates.
            </p>
          </div>

          {/* Final emphasis */}
          <div className="bg-gradient-to-br from-purple-950/40 via-blue-950/30 to-black border-2 border-purple-500/50 p-6 sm:p-8 md:p-10 my-12 relative" data-id="element-2745" data-mp="17in7v7d6pdsl">
            <div className="absolute top-0 left-0 w-10 h-10 border-l-2 border-t-2 border-purple-500" data-id="element-2746" data-mp="1h9hwgidwkph7" />
            <div className="absolute top-0 right-0 w-10 h-10 border-r-2 border-t-2 border-blue-500" data-id="element-2747" data-mp="1m8kuhivgtvfo" />
            <div className="absolute bottom-0 left-0 w-10 h-10 border-l-2 border-b-2 border-blue-500" data-id="element-2748" data-mp="1qafsade6tqop" />
            <div className="absolute bottom-0 right-0 w-10 h-10 border-r-2 border-b-2 border-purple-500" data-id="element-2749" data-mp="1v90sictfwad5" />

            <p className="text-2xl md:text-3xl font-bold text-center leading-relaxed" style={{
            textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
          }} data-id="element-2750" data-mp="3s22k2h5wlpm8">
              <span className="text-stone-100" data-id="element-2751" data-mp="x2t12kxmsug">And </span>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" data-id="element-2752" data-mp="3phwi2f0uo98b">
                THAT'S
              </span>
              <span className="text-stone-100" data-id="element-2753" data-mp="3fqs3hgf36qe0">
                {' '}
                what you get when you join{' '}
              </span>
              <span className="text-purple-400" data-id="element-2754" data-mp="3attbp4iwaviz">PSI PRO.</span>
            </p>
          </div>
        </div>
      </motion.div>
    </section>;
}